// Available credit on a customer statement (PD-16674).
//
// A statement's balance due is what the customer owes on open invoices.
// Credit the customer holds but has not yet applied to an invoice is shown
// beside it, never subtracted from it: money on the account (account funds)
// and, since PD-16674, the credit still open on credit memos. This module is
// the one place that adds those two parts up and words the line, so the
// email and the PDF say the same thing. The CRM and storefront screens use
// the figures and word the line through `t()` themselves.

import type { StatementActivityModel } from '@supply-mover-2-0/shared';
import { STATEMENT_CYCLE_CREDIT_MEMO_NOTE } from '@supply-mover-2-0/shared';
import { formatCurrency } from '../formatters';
import { fromCents, toCents } from './build-statement-activity';

export interface StatementAvailableCreditModel {
	/** Credit still open on credit memos (positive; 0 when none). */
	creditMemos: number;
	/** Money on the customer's account (positive; 0 when none). */
	accountFunds: number;
	/** `creditMemos + accountFunds`. */
	total: number;
	/**
	 * False for a statement stored before PD-16674: its balances already net
	 * its open credit memos, so only its account funds are available credit.
	 */
	includesCreditMemos: boolean;
	/**
	 * True when a cycle run will apply the credit memos to the automatic
	 * payment it scheduled for this statement, and there are memos to apply.
	 */
	appliesToCharge: boolean;
}

/**
 * The statement's available credit, split into its two parts. A negative
 * part (account funds overdrawn by a correction) counts as none: it is not
 * credit, and it is not part of the balance due either.
 */
export function statementAvailableCredit(
	activity: Pick<
		StatementActivityModel,
		| 'availableAccountCredit'
		| 'availableCreditMemos'
		| 'creditMemosApplyToCharge'
	>,
): StatementAvailableCreditModel {
	const memoCents = Math.max(0, toCents(activity.availableCreditMemos ?? 0));
	const fundsCents = Math.max(0, toCents(activity.availableAccountCredit));
	return {
		creditMemos: fromCents(memoCents),
		accountFunds: fromCents(fundsCents),
		total: fromCents(memoCents + fundsCents),
		includesCreditMemos: activity.availableCreditMemos != null,
		appliesToCharge: activity.creditMemosApplyToCharge === true && memoCents > 0,
	};
}

/**
 * The customer-facing available-credit line for the statement email and
 * PDF, or null when there is no credit to mention. Names both parts when
 * both exist, e.g. "Available credit: $61.12 (credit memos $22.47, account
 * funds $38.65), not yet applied to an invoice".
 *
 * A statement stored before PD-16674 keeps the wording it was sent with
 * ("Available account credit: …"), so re-rendering a stored statement does
 * not change what it said. A cycle statement whose automatic payment applies
 * the memos says so, because that payment will be less than the balance due.
 */
export function statementAvailableCreditText(
	activity: Pick<
		StatementActivityModel,
		| 'availableAccountCredit'
		| 'availableCreditMemos'
		| 'creditMemosApplyToCharge'
	>,
): string | null {
	const credit = statementAvailableCredit(activity);
	if (toCents(credit.total) === 0) {
		return null;
	}
	if (!credit.includesCreditMemos) {
		return `Available account credit: ${formatCurrency(credit.accountFunds)} (not yet applied to an invoice)`;
	}

	const parts: Array<string> = [];
	if (toCents(credit.creditMemos) > 0) {
		parts.push(`credit memos ${formatCurrency(credit.creditMemos)}`);
	}
	if (toCents(credit.accountFunds) > 0) {
		parts.push(`account funds ${formatCurrency(credit.accountFunds)}`);
	}
	// One part is named without repeating the amount the line already leads with.
	const breakdown =
		parts.length > 1
			? parts.join(', ')
			: toCents(credit.creditMemos) > 0
				? 'credit memos'
				: 'account funds';
	const line = `Available credit: ${formatCurrency(credit.total)} (${breakdown}), not yet applied to an invoice`;
	return credit.appliesToCharge
		? `${line}. ${STATEMENT_CYCLE_CREDIT_MEMO_NOTE}`
		: line;
}
